// The lazy route chunks, in one place, so two call sites can share the same
// `import()` thunks: routes.tsx / App.tsx wrap them in lazyWithReload(), and
// main.tsx starts the one the current URL needs BEFORE React mounts.
//
// Why (OMG-1836): on a cold member page load the chunk for the page was the
// fifth serial network step. Entry JS, then i18n, then the ContentfulProviders
// chunk (it wraps AppRoutes, so the whole route tree waited on it), then the
// auth check and getMe, and only then did React reach the route element and
// trigger its import(). Starting the import from module scope moves that
// download to the same moment the entry chunk finishes evaluating, in parallel
// with everything else; the module map dedupes it, so by the time lazy()
// calls the same thunk the promise is already resolved.
//
// Vite emits one chunk per import() target regardless of how many thunks
// reference it, and each thunk here keeps a static specifier, which is all the
// bundler needs. Keep the specifiers in sync with the route table: a chunk
// listed here but not in routes.tsx is a harmless extra download, a route
// imported inline in routes.tsx is simply never prefetched.

import type { NewMemberHomeData } from "@types.ts";

import {
  AUTH_ROUTES,
  LOGOUT_ROUTES,
  MEMBER_ROUTES,
  PUBLIC_ROUTES,
} from "@lib/routePaths";

import { NMH_LOOKUP } from "@/config";
import { queryClient } from "@/utils/QueryClientProvider";

export const routeChunks = {
  ArticlePage: () => import("@pages/ArticlePage"),
  AuthPage: () => import("@pages/Auth/AuthPage"),
  WhoAmIPage: () => import("@pages/Auth/WhoAmI"),
  PostCheckout: () =>
    import("@pages/checkout/PostCheckout").then((m) => ({
      default: m.PostCheckout,
    })),
  CollectionPage: () => import("@pages/CollectionPage"),
  TechniquePageContainer: () =>
    import(
      "@pages/collections/components/layouts/components/TechniquePageContainer"
    ).then((m) => ({ default: m.TechniquePageContainer })),
  TechniquePageLayout: () =>
    import("@pages/collections/components/layouts/TechniquePageLayout").then(
      (m) => ({ default: m.TechniquePageLayout }),
    ),
  // Lean-back mode (OMG-2747): the watch view and its one-element player.
  // Only /techniques/:id/watch pays for it.
  LeanBackView: () =>
    import("@pages/collections/leanback/LeanBackView").then((m) => ({
      default: m.LeanBackView,
    })),
  DateNightPage: () => import("@pages/DateNightPage"),
  GiftCard: () => import("@pages/gift/GiftCard"),
  GiftPage: () => import("@pages/gift/GiftPage"),
  NewMemberHome: () => import("@pages/NewMemberHome"),
  Redeem: () => import("@pages/redeem/redeem"),
  SimulationPage: () => import("@pages/SimulationPage"),
  UpsellPage: () =>
    import("@pages/UpsellPage").then((m) => ({ default: m.UpsellPage })),
  // Not a route: the Contentful provider stack App.tsx mounts around the
  // member routes. Listed here because every member page needs it before it
  // can render, so it is prefetched with the page chunk.
  ContentfulProviders: () => import("@/components/ContentfulProviders"),
};

export type RouteChunk = keyof typeof routeChunks;

const MEMBERS_BASENAME = "/members";

// The route table sees basename-relative paths ("/home"), module scope sees
// the browser path ("/members/home"). Accept both.
export const stripMembersBasename = (pathname: string): string =>
  pathname === MEMBERS_BASENAME || pathname.startsWith(`${MEMBERS_BASENAME}/`)
    ? pathname.slice(MEMBERS_BASENAME.length) || "/"
    : pathname;

const under = (pathname: string, base: string): boolean =>
  pathname === base || pathname.startsWith(`${base}/`);

// "/collection/:id" -> "/collection"
const routeBase = (route: string): string => route.split("/:")[0];

const isAuthPath = (pathname: string): boolean =>
  AUTH_ROUTES.includes(pathname) || LOGOUT_ROUTES.includes(pathname);

/** Login, signup, password and logout routes, at either basename. */
export const isAuthRoute = (pathname: string): boolean =>
  isAuthPath(stripMembersBasename(pathname));

/**
 * The chunks the page at `pathname` renders with, page chunk first.
 * Empty for /checkout (static import, nothing to prefetch), for the root
 * (Index redirects after an auth check, the target is not known yet) and for
 * the locale-prefixed gift URLs (full-document redirect, see routes.tsx).
 */
export const chunksForPath = (pathname: string): RouteChunk[] => {
  const p = stripMembersBasename(pathname);

  if (under(p, PUBLIC_ROUTES.CHECKOUT)) return [];
  if (under(p, PUBLIC_ROUTES.GIFTS_CARD)) return ["GiftCard"];
  if (under(p, PUBLIC_ROUTES.GIFTS)) return ["GiftPage"];
  if (under(p, PUBLIC_ROUTES.REDEEM)) return ["Redeem"];
  if (/^\/[^/]+\/(gifts|redeem)(\/|$)/.test(p)) return [];
  if (isAuthPath(p)) return ["AuthPage"];
  if (p === "/") return [];

  // Everything else lives inside the authenticated route block, which App.tsx
  // wraps in ContentfulProviders.
  const member: RouteChunk[] = ["ContentfulProviders"];
  if (under(p, MEMBER_ROUTES.HOME)) return [...member, "NewMemberHome"];
  if (under(p, routeBase(MEMBER_ROUTES.COLLECTION)))
    return [...member, "CollectionPage"];
  if (under(p, routeBase(MEMBER_ROUTES.ARTICLE)))
    return [...member, "ArticlePage"];
  if (under(p, routeBase(MEMBER_ROUTES.DATE_NIGHT)))
    return [...member, "DateNightPage"];
  if (p === MEMBER_ROUTES.TECHNIQUES) return [...member, "TechniquePageLayout"];
  if (/^\/techniques\/[^/]+\/watch\/?$/.test(p))
    return [...member, "TechniquePageLayout", "LeanBackView"];
  if (under(p, MEMBER_ROUTES.TECHNIQUES))
    return [...member, "TechniquePageLayout", "TechniquePageContainer"];
  if (under(p, MEMBER_ROUTES.UPSELL)) return [...member, "UpsellPage"];
  if (under(p, MEMBER_ROUTES.WHOAMI)) return [...member, "WhoAmIPage"];
  if (under(p, routeBase(MEMBER_ROUTES.POST_CHECKOUT)))
    return [...member, "PostCheckout"];
  if (under(p, MEMBER_ROUTES.SIMULATION_TEST))
    return [...member, "SimulationPage"];
  // Unknown member path: routes.tsx redirects it to /home.
  return [...member, "NewMemberHome"];
};

/** Whether App.tsx should mount the Contentful provider stack for this path. */
export const isContentfulRoute = (pathname: string): boolean =>
  chunksForPath(pathname).includes("ContentfulProviders");

/**
 * Whether main.tsx should start techniques.json for the page at `pathname`:
 * the routes behind RequireTechniquesNamespace in routes.tsx (every member
 * route except /post-checkout, which only reads checkoutPage.* keys and then
 * hard-reloads into /members/, a fresh i18n init fetched with cache:
 * "no-store", so warming it there would be wasted bytes), plus the bare
 * /members/ root. The root is where that reload lands, the first member page
 * every new buyer sees: Index redirects a signed-in member to /home, and
 * without this the namespace would only start after the auth check, the
 * redirect and the gate, serialized where it used to be parallel. A
 * signed-out visitor is sent to /login, which warms it on idle anyway, so
 * the extra cost there is close to nothing. Keep in sync with the route that
 * carries the gate.
 */
export const needsTechniquesNamespace = (pathname: string): boolean => {
  const p = stripMembersBasename(pathname);
  if (p === "/") return true;
  return (
    isContentfulRoute(pathname) &&
    !under(p, routeBase(MEMBER_ROUTES.POST_CHECKOUT))
  );
};

// Warm the react-query cache with the member-home Contentful entry, the
// query ContentfulProvider runs on mount (same id, same options, so the
// same query key; react-query dedupes an in-flight fetch). The helper module
// pulls the `contentful` client chunk, which is why it is imported lazily
// here instead of at the top: a static import would hoist that chunk into
// the entry for every route, undoing the split App.tsx documents.
export const prefetchMemberHomeEntry = (): Promise<
  NewMemberHomeData | undefined
> =>
  import("@lib/helpers/getContentFulEntryQueryOptions")
    .then(async ({ getContentfulGetEntryQueryOptions }) => {
      const options = getContentfulGetEntryQueryOptions<NewMemberHomeData>(
        NMH_LOOKUP.en,
        { include: 3 },
      );
      await queryClient.prefetchQuery(options);
      // Same cast ContentfulProvider applies to its useQuery result.
      return queryClient.getQueryData(options.queryKey) as
        | NewMemberHomeData
        | undefined;
    })
    .catch(() => undefined);

export const whenIdle = (fn: () => void): void => {
  if (typeof window === "undefined") return;
  // Safari has no requestIdleCallback.
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(fn, { timeout: 4000 });
  } else {
    window.setTimeout(fn, 2000);
  }
};

/**
 * Start downloading what the page at `pathname` will need. Called once from
 * main.tsx at module scope, before the i18n await and before React mounts.
 *
 * Never throws and never blocks: every promise is detached and its rejection
 * swallowed. A chunk that fails here (stale tab after a deploy, network) is
 * requested again by lazyWithReload when the route renders, and that path
 * owns the reload recovery (OMG-2594).
 */
export const prefetchForPath = (pathname: string): void => {
  try {
    const chunks = chunksForPath(pathname);
    for (const name of chunks) {
      routeChunks[name]().catch(() => {});
    }

    if (chunks.includes("ContentfulProviders")) {
      // Member page: the provider's query is on the critical path, fire it
      // now so it overlaps the auth check instead of following it.
      void prefetchMemberHomeEntry();
    } else if (isAuthPath(stripMembersBasename(pathname))) {
      // Login page: nothing here reads Contentful, but the member home the
      // user lands on after signing in does. Warm its chunk and entry once
      // the login form is on screen and the browser is idle, so the
      // post-login /home paints from cache as it did when the provider
      // stack still mounted on /login.
      whenIdle(() => {
        routeChunks.ContentfulProviders().catch(() => {});
        void prefetchMemberHomeEntry();
      });
    }
  } catch {
    // Belt for the module-scope call site: nothing in here may ever stop
    // the app from mounting.
  }
};
